// THE CHAT RESUME MACHINERY (deep-sweep 2026-07-09 F4; extended by BUILD-DEC-098) — the durable resume
// pointer + the reload-resume hook, split out of useChat.ts for the file-size ceiling. The pointer is an
// OPAQUE {sessionId, runId} pair in localStorage — never a turn, never business state; agent B stays the
// record owner (Constraint 4). Restore always revalidates the pointer through the owner-scoped server read.
// A stale/foreign pointer restores nothing and is dropped, including after a browser or login change.

import { useEffect } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { mayAttachPreview } from './chat-wake.ts';
import type { ChatTranscriptView } from './projections.ts';

/**
 * RESUME ACROSS A RELOAD (deep-sweep 2026-07-09 F4): the dock is "on the record", so a reload re-READS
 * the owned record instead of silently starting fresh. Persists ONLY an opaque {sessionId, runId}
 * POINTER (localStorage, durable across tabs/browser restarts) — never a turn, never business state;
 * agent B stays the record owner (Constraint 4). Restore is fail-silent: a stale/foreign pointer restores
 * nothing because the owner-scoped read refuses it, and the rejected pointer is dropped.
 */
export function useResumeAcrossReload(
  api: ReturnType<typeof useSurfaceApi>,
  deps: {
    readonly sessionIdRef: React.MutableRefObject<string | null>;
    readonly mountedRef: React.MutableRefObject<boolean>;
    readonly setTranscript: (view: ChatTranscriptView) => void;
    readonly setPriorRuns: React.Dispatch<React.SetStateAction<readonly ChatTranscriptView[]>>;
    readonly setActiveSessionId: (sessionId: string | null) => void;
    /** Subscribe to a still-running resumed run so its preview + activity stream in and reconcile on end
     *  (review fix — a resumed running run otherwise showed "working…" forever with no subscription). */
    readonly subscribeIfRunning: (runId: string) => void;
    readonly transcriptRunId: string | undefined;
  },
): void {
  const { sessionIdRef, mountedRef, setTranscript, setPriorRuns, setActiveSessionId } = deps;
  const { subscribeIfRunning } = deps;
  useEffect(() => {
    const stored = readResumePointer();
    if (stored === null) return;
    sessionIdRef.current = stored.sessionId;
    setActiveSessionId(stored.sessionId);
    // Re-read the WHOLE conversation (every run — BUILD-DEC-098): resuming to only the last run's
    // transcript silently dropped every earlier exchange of a multi-send session.
    api
      .getChatSession(stored.sessionId)
      .then((view) => {
        if (!mountedRef.current || sessionIdRef.current !== stored.sessionId) return;
        if (view.runs.length === 0) return;
        const latest = view.runs[view.runs.length - 1]!;
        setPriorRuns(view.runs.slice(0, -1));
        setTranscript(latest);
        // The SAME preview rule the open/wake path uses (mayAttachPreview): a routine restored from the
        // pointer is read, never subscribed — its runs are the platform's and both per-run surfaces refuse
        // a non-initiator, so attaching would strand the dock on a preview that can never arrive.
        if (mayAttachPreview(view, latest)) subscribeIfRunning(latest.runId);
      })
      .catch(() => clearResumePointer());
    // Mount-only: the pointer is read once; later transcripts persist below.
  }, [
    api,
    sessionIdRef,
    mountedRef,
    setTranscript,
    setPriorRuns,
    setActiveSessionId,
    subscribeIfRunning,
  ]);
  useEffect(() => {
    if (deps.transcriptRunId !== undefined && sessionIdRef.current !== null) {
      writeResumePointer({ sessionId: sessionIdRef.current, runId: deps.transcriptRunId });
    }
  }, [deps.transcriptRunId, sessionIdRef]);
}

/** The durable resume pointer (F4): an OPAQUE {sessionId, runId} pair — never a turn or a business
 * entity. Browser storage is only a hint: the owner-scoped server read is authoritative on every restore. */
const RESUME_KEY = 'ritona.chat.resume';

interface ResumePointer {
  readonly sessionId: string;
  /** The last-known run of the conversation, when one exists. OPTIONAL: a REFUSED/failed send that never
   *  resolved a run still persists a pointer to its conversation (audit F5) — restore re-reads the WHOLE
   *  owned conversation from the sessionId alone, so the runId is a subscribe hint, never restore-critical. */
  readonly runId?: string;
}

function readResumePointer(): ResumePointer | null {
  try {
    const raw = window.localStorage.getItem(RESUME_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<ResumePointer>;
    // The sessionId is what restore actually reads; the runId is an optional hint (a refused send has none).
    if (typeof parsed.sessionId === 'string') {
      return typeof parsed.runId === 'string'
        ? { sessionId: parsed.sessionId, runId: parsed.runId }
        : { sessionId: parsed.sessionId };
    }
  } catch {
    /* storage unavailable / malformed — resume silently skipped */
  }
  return null;
}

export function writeResumePointer(pointer: ResumePointer): void {
  try {
    window.localStorage.setItem(RESUME_KEY, JSON.stringify(pointer));
  } catch {
    /* storage unavailable — the dock simply starts fresh next load */
  }
}

export function clearResumePointer(): void {
  try {
    window.localStorage.removeItem(RESUME_KEY);
  } catch {
    /* nothing to clear */
  }
}
